import { useEditMode } from 'purchasers/editMode'
import { omit } from 'ramda'
import { useRef, useState } from 'react'
import { Popover, SizableText, View, XStack, YStack } from 'tamagui'
import { Label } from './label'
import { SlotWrapper } from './input'
import { X } from '@tamagui/lucide-icons-2'

export interface AutocompleteProps<
    T,
    Multiple extends boolean | undefined,
    DisableClearable extends boolean | undefined,
    FreeSolo extends boolean | undefined,
> {
    options: T[]
    value?: Multiple extends true ? T[] : T | null
    onChange?: (event: any, value: Multiple extends true ? T[] : T | null) => void
    onInputChange?: (event: any, value: string) => void
    getOptionLabel?: (option: T) => string
    isOptionEqualToValue?: (option: T, value: T) => boolean
    groupBy?: (option: T) => string
    getOptionDisabled?: (option: T) => boolean
    multiple?: Multiple
    freeSolo?: FreeSolo
    disableClearable?: DisableClearable
    disabled?: boolean
    placeholder?: string
    label?: string
    loading?: boolean
    required?: boolean
    error?: boolean | any
    slotProps?: { input?: any; [key: string]: any }
    inputRef?: any
    onBlur?: (e: any) => void
    sx?: any
    id?: string
    [key: string]: any
}

export const Autocomplete = <
    T,
    Multiple extends boolean | undefined,
    DisableClearable extends boolean | undefined,
    FreeSolo extends boolean | undefined,
>(
    props: AutocompleteProps<T, Multiple, DisableClearable, FreeSolo>,
) => {
    const { editMode } = useEditMode()
    const [inputText, setInputText] = useState('')
    const [isOpen, setIsOpen] = useState(false)
    const [highlightedIndex, setHighlightedIndex] = useState(-1)
    const inputRef = useRef<HTMLInputElement>(null)

    const getLabel = (option: T): string => {
        if (!option) return ''
        if (props.getOptionLabel) {
            return props.getOptionLabel(option)
        }
        if (typeof option === 'string') return option
        return String(option)
    }

    const singleValueRender = (val: T) => {
        if (!val) return ''
        return getLabel(val)
    }

    const valueRender = (): string =>
        props.multiple
            ? ((props.value as any[]) ?? []).map(singleValueRender).join(', ')
            : singleValueRender(props.value as any)

    if (!editMode) {
        return (
            <YStack>
                {props.label && <Label>{props.label}</Label>}
                <SizableText>{valueRender()}</SizableText>
            </YStack>
        )
    }

    // Filter options based on input text
    const filteredOptions = props.options.filter(option => {
        const label = getLabel(option)
        return label.toLowerCase().includes(inputText.toLowerCase())
    })

    // Group options if groupBy is provided
    const groupedOptions: { group: string; options: T[] }[] = props.groupBy
        ? Object.entries(
            filteredOptions.reduce<Record<string, T[]>>((acc, option) => {
                const group = props.groupBy!(option)
                if (!acc[group]) acc[group] = []
                acc[group].push(option)
                return acc
            }, {})
        ).map(([group, options]) => ({ group, options }))
        : [{ group: '', options: filteredOptions }]

    const handleSelect = (option: T) => {
        if (props.multiple) {
            const currentValue = (props.value as T[]) ?? []
            const isAlreadySelected = currentValue.some(v =>
                props.isOptionEqualToValue
                    ? props.isOptionEqualToValue(option, v)
                    : v === option
            )
            const newValue = isAlreadySelected
                ? currentValue.filter(v =>
                    !(props.isOptionEqualToValue
                        ? props.isOptionEqualToValue(option, v)
                        : v === option)
                )
                : [...currentValue, option]
            props.onChange?.(null, newValue as any)
        } else {
            props.onChange?.(null, option as any)
            setInputText(getLabel(option))
            setIsOpen(false)
        }
    }

    const handleRemoveTag = (option: T) => {
        if (props.multiple) {
            const currentValue = (props.value as T[]) ?? []
            const newValue = currentValue.filter(v =>
                !(props.isOptionEqualToValue
                    ? props.isOptionEqualToValue(option, v)
                    : v === option)
            )
            props.onChange?.(null, newValue as any)
        }
    }

    const handleInputChange = (text: string) => {
        setInputText(text)
        setIsOpen(true)
        props.onInputChange?.(
            { target: { value: text } } as any,
            text
        )
    }

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault()
            setHighlightedIndex(prev =>
                prev < filteredOptions.length - 1 ? prev + 1 : 0
            )
        } else if (e.key === 'ArrowUp') {
            e.preventDefault()
            setHighlightedIndex(prev =>
                prev > 0 ? prev - 1 : filteredOptions.length - 1
            )
        } else if (e.key === 'Enter') {
            e.preventDefault()
            if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
                handleSelect(filteredOptions[highlightedIndex])
            }
        } else if (e.key === 'Escape') {
            setIsOpen(false)
        }
    }

    return (
        <YStack>
            {props.label && <Label>{props.label}</Label>}
            <Popover
                open={isOpen}
                onOpenChange={open => {
                    setIsOpen(open)
                    if (!open) setInputText('')
                }}
                placement="bottom-start"
                allowFlip
                offset={4}
                stayInFrame={{ padding: 8 }}
            >
                <Popover.Anchor asChild>
                    {/* Custom input container with inline chips */}
                    <XStack
                        borderWidth={1}
                        borderColor={props.error ? '$statusDanger' : '$borderColor'}
                        borderRadius="$4"
                        paddingHorizontal="$3.5"
                        paddingVertical="$2"
                        minHeight={44}
                        backgroundColor="$backgroundStrong"
                        color="$color"
                        flexWrap="wrap"
                        gap="$1"
                        alignItems="center"
                        hoverStyle={{
                            borderColor: props.error ? '$statusDanger' : '$borderColorHover',
                        }}
                        focusWithinStyle={{
                            borderColor: props.error ? '$statusDanger' : '$borderColorFocus',
                            outlineWidth: 0,
                        }}
                        cursor="text"
                        onPress={() => {
                            // Focus the input when clicking the container
                            const input = inputRef.current || props.slotProps?.input?.ref?.current
                            if (input) input.focus()
                        }}
                        opacity={props.disabled ? 0.6 : 1}
                        pointerEvents={props.disabled ? 'none' : 'auto'}
                    >
                        {/* Tags for multiple mode - rendered inline */}
                        {props.multiple &&
                            ((props.value as T[]) ?? []).map((val, i) => (
                                <XStack
                                    key={i}
                                    backgroundColor="$primaryLight"
                                    borderRadius="$2"
                                    paddingHorizontal="$2"
                                    paddingVertical="$1"
                                    alignItems="center"
                                    gap="$1"
                                    height={28}
                                >
                                    <SizableText size="$2">{getLabel(val)}</SizableText>
                                    <View
                                        cursor="pointer"
                                        onPress={(e: any) => {
                                            e.stopPropagation()
                                            handleRemoveTag(val)
                                        }}
                                        hoverStyle={{ opacity: 0.7 }}
                                    >
                                        <X size={12} />
                                    </View>
                                </XStack>
                            ))}

                        {/* Native input element for typing */}
                        <input
                            ref={props.slotProps?.input?.ref ?? inputRef}
                            value={
                                props.multiple
                                    ? inputText
                                    : isOpen
                                        ? inputText
                                        : props.value
                                            ? getLabel(props.value as T)
                                            : ''
                            }
                            onChange={e => handleInputChange(e.target.value)}
                            onFocus={() => {
                                setIsOpen(true)
                                if (!props.multiple && props.value) {
                                    // Clear input text when focusing for search in single mode
                                    setInputText('')
                                }
                            }}
                            onBlur={(e: any) => {
                                // Delay close to allow click on option
                                setTimeout(() => {
                                    setIsOpen(false)
                                    setInputText('')
                                }, 200)
                                props.onBlur?.(e)
                            }}
                            onKeyDown={handleKeyDown as any}
                            placeholder={
                                props.multiple && ((props.value as T[]) ?? []).length > 0
                                    ? ''
                                    : props.placeholder
                            }
                            disabled={props.disabled}
                            style={{
                                border: 'none',
                                outline: 'none',
                                background: 'transparent',
                                flex: 1,
                                minWidth: 120,
                                fontSize: 14,
                                fontFamily: 'inherit',
                                height: 28,
                                color: 'inherit',
                            }}
                        />
                    </XStack>
                </Popover.Anchor>

                {/* Options menu. Popover renders this in a portal, so the FormSection
                    Card's overflow:hidden can't clip it, and floating-ui keeps it
                    anchored under the field (flipping up when there's no room below).
                    disableFocusScope keeps the text input typeable. */}
                {(filteredOptions.length > 0 || props.loading) && (
                    <Popover.Content
                        disableFocusScope
                        padding={0}
                        backgroundColor="$backgroundRaised"
                        borderWidth={1}
                        borderColor="$borderColor"
                        borderRadius="$2"
                        elevation={4}
                        overflow="hidden"
                        maxHeight={300}
                        style={{ width: 'var(--tamagui-popper-anchor-width)' }}
                        // Keep input focus on option click so blur doesn't close first.
                        onMouseDown={(e: any) => e.preventDefault()}
                        animation="quick"
                        enterStyle={{ opacity: 0, y: -4 }}
                        exitStyle={{ opacity: 0, y: -4 }}
                    >
                        {filteredOptions.length > 0 ? (
                            <View style={{ maxHeight: 300, overflowY: 'auto', width: '100%' }}>
                                {groupedOptions.map(({ group, options: groupOpts }) => (
                                    <YStack key={group}>
                                        {group && (
                                            <SizableText
                                                size="$2"
                                                fontWeight="bold"
                                                paddingHorizontal="$3"
                                                paddingVertical="$2"
                                                color="$colorMuted"
                                            >
                                                {group}
                                            </SizableText>
                                        )}
                                        {groupOpts.map((option, idx) => {
                                            const globalIdx = filteredOptions.indexOf(option)
                                            const isDisabled = props.getOptionDisabled?.(option) ?? false
                                            const isSelected = props.multiple
                                                ? ((props.value as T[]) ?? []).some(v =>
                                                      props.isOptionEqualToValue
                                                          ? props.isOptionEqualToValue(option, v)
                                                          : v === option,
                                                  )
                                                : props.isOptionEqualToValue
                                                    ? props.isOptionEqualToValue(option, props.value as T)
                                                    : option === props.value

                                            return (
                                                <View
                                                    key={idx}
                                                    onPress={() => !isDisabled && handleSelect(option)}
                                                    cursor={isDisabled ? 'default' : 'pointer'}
                                                    paddingHorizontal="$3"
                                                    paddingVertical="$2"
                                                    backgroundColor={
                                                        globalIdx === highlightedIndex
                                                            ? '$backgroundHover'
                                                            : isSelected
                                                                ? '$backgroundPress'
                                                                : 'transparent'
                                                    }
                                                    opacity={isDisabled ? 0.5 : 1}
                                                    hoverStyle={{ backgroundColor: '$backgroundHover' }}
                                                >
                                                    <SizableText size="$3">{getLabel(option)}</SizableText>
                                                </View>
                                            )
                                        })}
                                    </YStack>
                                ))}
                            </View>
                        ) : (
                            <View padding="$3">
                                <SizableText>Loading...</SizableText>
                            </View>
                        )}
                    </Popover.Content>
                )}
            </Popover>
        </YStack>
    )
}

export const SlotAutocomplete = <
    T,
    Multiple extends boolean | undefined,
    DisableClearable extends boolean | undefined,
    FreeSolo extends boolean | undefined,
>({
    field,
    formState,
    ...props
}: Omit<AutocompleteProps<T, Multiple, DisableClearable, FreeSolo>, 'error'> & {
    field: any
    formState: any
}) => (
    <SlotWrapper
        field={field}
        formState={formState}
        {...props}
    >
        <Autocomplete {...(omit(['fieldState', 'label'], props) as any)} />
    </SlotWrapper>
)
